/**
 * Service Attendee Report Wrapper
 * Bridges the enhanced component with the existing reports system
 */

import React, { useState, useEffect } from 'react';
import EnhancedServiceAttendeeReport from './EnhancedServiceAttendeeReport';
import axios from 'axios';

// Simple inline loading spinner component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin h-6 w-6 text-blue-600">
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  </div>
);

const ServiceAttendeeReportWrapper = ({
  reportType,
  parameters,
  onError,
  className = ''
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch report data
  useEffect(() => {
    const fetchReportData = async () => {
      if (reportType !== 'service-attendees') return;

      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Fetching Service Attendee Report data with parameters:', parameters);

        // Build query parameters
        const queryParams = new URLSearchParams();
        if (parameters.startDate) queryParams.append('startDate', parameters.startDate);
        if (parameters.endDate) queryParams.append('endDate', parameters.endDate);
        if (parameters.serviceId) queryParams.append('serviceId', parameters.serviceId);
        if (parameters.excludeCancelled) queryParams.append('excludeCancelled', 'true');

        const response = await axios.get(`/api/reports/service-attendees?${queryParams.toString()}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        console.log('🔍 Service Attendee Report API response:', response.data);

        // The service-attendees endpoint returns data directly, not wrapped in success/data structure
        if (response.data && response.data.data && response.data.columns) {
          setData({
            data: response.data.data,
            columns: response.data.columns,
            metadata: response.data.metadata,
            summary: response.data.summary
          });
        } else {
          throw new Error('Invalid response structure from service attendees API');
        }
      } catch (err) {
        console.error('Error fetching Service Attendee Report:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch report data';
        setError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportType, parameters, onError]);

  if (reportType !== 'service-attendees') {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
        <span className="ml-3 text-gray-600">Loading Service Attendee Report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error Loading Report
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        No data available for Service Attendee Report
      </div>
    );
  }

  return (
    <EnhancedServiceAttendeeReport
      data={data}
      className={className}
    />
  );
};

export default ServiceAttendeeReportWrapper;